import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { 
  db, 
  collection, 
  query, 
  where, 
  onSnapshot,
  doc,
  getDoc
} from '../firebase';
import { 
  Package, 
  Search, 
  Tag, 
  ChevronRight,
  Loader2,
  X,
  Plus,
  Minus,
  ShoppingCart,
  ArrowRight,
  MoveRight,
  Globe,
  Monitor,
  Smartphone,
  QrCode,
  Copy,
  CheckCircle2,
  ShoppingBag
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { QRCodeSVG } from 'qrcode.react';
import { notify } from '../services/notify';
import { cn } from '../lib/utils';

interface Item {
  id: string;
  name: string;
  description: string;
  unitPrice: number;
  imageUrl?: string;
  category?: string;
  code: string;
  barcode?: string;
}

interface CatalogSettings {
  theme?: 'Standard' | 'Brutalist' | 'Minimal' | 'Technical' | 'Cherry Blossom';
  customHeroTitle?: string;
  customHeroDescription?: string;
  primaryColor?: string;
  accentColor?: string;
  fontFamily?: string;
  hidePrices?: boolean;
  showInventory?: boolean;
  whatsappNumber?: string;
}

interface Organization {
  id: string;
  name: string;
  catalogSettings?: CatalogSettings;
}

interface PublicCatalogViewProps {
  orgId?: string;
}

const PublicCatalogView: React.FC<PublicCatalogViewProps> = ({ orgId: propOrgId }) => {
  const params = useParams<{ orgId: string }>();
  const orgId = propOrgId || params.orgId;
  const [items, setItems] = useState<Item[]>([]);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cart, setCart] = useState<{item: Item, quantity: number}[]>([]);
  const [copied, setCopied] = useState(false);
  const [showItemQR, setShowItemQR] = useState(false);

  const handleCheckout = () => {
    if (cart.length === 0) {
      notify.error('Your bag is empty.');
      return;
    }

    const whatsappNumber = organization?.catalogSettings?.whatsappNumber || '';
    if (!whatsappNumber) {
      notify.error('Checkout is temporarily unavailable. Please contact us directly.');
      return;
    }

    let text = `*New Order Inquiry*\n\n`;
    text += `*Items:*\n`;
    cart.forEach(cartItem => {
      text += `- ${cartItem.item.name} (Qty: ${cartItem.quantity}) - EGP ${((cartItem.item?.unitPrice || 0) * (cartItem.quantity || 1)).toLocaleString()}\n`;
    });
    
    text += `\n*Total: EGP ${(subtotal || 0).toLocaleString()}*`;
    text += `\n\nI would like to proceed with this order.`;

    const encodedText = encodeURIComponent(text);
    window.open(`https://wa.me/${whatsappNumber}?text=${encodedText}`, '_blank');
  };

  useEffect(() => {
    if (!orgId) return;

    const isLocal = typeof window !== 'undefined' && ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
    if (isLocal) {
      let cancelled = false;
      fetch(`/api/public/catalog/${orgId}`)
        .then(async (res) => {
          if (!res.ok) throw new Error('Could not load catalog');
          return res.json();
        })
        .then((data) => {
          if (cancelled) return;
          setOrganization(data.organization as Organization);
          setItems((data.items || []) as Item[]);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error loading public catalog:', err);
          if (!cancelled) setLoading(false);
        });
      return () => { cancelled = true; };
    }

    // Fetch Organization Data
    const fetchOrg = async () => {
      try {
        const orgDoc = await getDoc(doc(db, 'organizations', orgId));
        if (orgDoc.exists()) {
          setOrganization({ id: orgDoc.id, ...orgDoc.data() } as Organization);
        }
      } catch (err) {
        console.error("Error fetching organization:", err);
      }
    };
    fetchOrg();

    // Fetch Public Items
    const q = query(
      collection(db, 'items'),
      where('orgId', '==', orgId),
      where('enablePOS', '==', true),
      where('status', '==', 'active')
    );

    // Add a safety timeout for loading
    const loadTimeout = setTimeout(() => {
      setLoading(false);
    }, 15000); // 15 seconds max wait

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const itemsData = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as Item));
      setItems(itemsData);
      setLoading(false);
      clearTimeout(loadTimeout);
    }, (error) => {
      console.error("Error loading items:", error);
      setLoading(false);
      clearTimeout(loadTimeout);
    });

    return () => {
      unsubscribe();
      clearTimeout(loadTimeout);
    };
  }, [orgId]);

  const categories = useMemo(() => {
    const cats = new Set(items.map(item => item.category).filter(Boolean));
    return ['All', ...Array.from(cats)];
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          item.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (item.code || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === 'All' || item.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [items, searchQuery, activeCategory]);

  const addToCart = (item: Item) => {
    setCart(prev => {
      const existing = prev.find(p => p.item.id === item.id);
      if (existing) {
        return prev.map(p => p.item.id === item.id ? { ...p, quantity: p.quantity + 1 } : p);
      }
      return [...prev, { item, quantity: 1 }];
    });
  };

  const removeFromCart = (itemId: string) => {
    setCart(prev => prev.filter(p => p.item.id !== itemId));
  };

  const updateQuantity = (itemId: string, delta: number) => {
    setCart(prev => prev.map(p => {
      if (p.item.id === itemId) {
        const newQty = Math.max(1, p.quantity + delta);
        return { ...p, quantity: newQty };
      }
      return p;
    }));
  };

  const subtotal = cart.reduce((acc, curr) => acc + (curr.item.unitPrice * curr.quantity), 0);
  
  const CHERRY_PALETTE = {
    nightBordeaux: '#590D22',
    darkAmaranth: '#800F2F',
    cherryRose: '#A4133C',
    rosewood: '#C9184A',
    bubblegum: '#FF4D6D',
    cottonCandy: '#FF8FA3',
    cherryBlossom: '#FFB3C1',
    pastelPetal: '#FFCCD5',
    lavenderBlush: '#FFF0F3',
  };

  const settings = organization?.catalogSettings || { theme: 'Brutalist' };
  const theme = settings.theme || 'Brutalist';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center p-8">
        <div className="flex flex-col items-center gap-8 text-center max-w-sm">
          <div className="relative">
            <motion.div
              animate={{ 
                rotate: 360,
                borderRadius: ["2rem", "3rem", "2rem"]
              }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
              className="w-24 h-24 bg-black shadow-2xl flex items-center justify-center relative z-10"
            >
               <Package className="w-10 h-10 text-white" />
            </motion.div>
            <motion.div 
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute inset-0 bg-black/20 blur-2xl rounded-full -z-10"
            />
          </div>
          <div className="space-y-4">
            <div className="space-y-1">
              <h2 className="text-black font-black uppercase tracking-[0.4em] text-xs italic">Synchronizing Assets</h2>
              <p className="text-[10px] font-medium opacity-40 uppercase tracking-widest leading-relaxed">
                Fetching catalogs, refining visuals, and <br/>preparing your experience...
              </p>
            </div>
            <div className="w-48 h-1 bg-black/5 rounded-full overflow-hidden mx-auto">
               <motion.div 
                 initial={{ width: "0%" }}
                 animate={{ width: "100%" }}
                 transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                 className="h-full bg-black"
               />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isBrutalist = theme === 'Brutalist';
  const isCherry = theme === 'Cherry Blossom';
  const isMinimal = theme === 'Minimal';
  const isTechnical = theme === 'Technical';

  return (
    <div 
      className={cn(
        "min-h-screen font-sans selection:bg-black selection:text-white transition-colors duration-500",
        isBrutalist ? "bg-white text-black" : 
        isCherry ? "cherry-mode" : 
        isMinimal ? "bg-[#FAFAFA] text-black font-light" : 
        isTechnical ? "bg-[#0A0A0A] text-[#00FF41] font-mono" : 
        "bg-white text-black"
      )}
      style={isCherry ? { backgroundColor: CHERRY_PALETTE.lavenderBlush, color: CHERRY_PALETTE.nightBordeaux } : {}}
    >
      {/* Navigation */}
      <nav 
        className={cn(
          "sticky top-0 z-50 px-6 py-4 transition-all duration-300",
          isBrutalist ? "border-b-2 border-black bg-white/90 backdrop-blur-md" : 
          isCherry ? "bg-white/80 backdrop-blur-md border-b-[6px] border-double shadow-sm" : 
          isMinimal ? "bg-white/50 backdrop-blur-xl" : 
          isTechnical ? "bg-black/80 backdrop-blur-md border-b border-[#00FF41]/20 shadow-[0_0_20px_rgba(0,255,65,0.05)]" : 
          "border-b border-black/5 bg-white/90 backdrop-blur-md"
        )}
        style={isCherry ? { borderBottomColor: CHERRY_PALETTE.cherryBlossom } : {}}
      >
        <div className="max-w-[1800px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-12">
            <h1 className={cn(
              "text-2xl font-black tracking-tighter uppercase leading-none",
              isCherry && "font-serif italic capitalize tracking-normal",
              isMinimal && "font-normal tracking-[0.2em] normal-case",
              isTechnical && "font-mono tracking-widest"
            )}>
              {organization?.name || 'CATALOG'}
              <span className={cn(
                isCherry ? "text-[#FF4D6D]" : 
                isTechnical ? "text-[#00FF41] animate-pulse" : 
                "text-primary"
              )}>.</span>
            </h1>
            <div className={cn(
              "hidden lg:flex items-center gap-8 uppercase font-bold text-[10px] tracking-[0.2em] opacity-40",
              isTechnical && "text-[#00FF41] font-mono opacity-60",
              isMinimal && "font-light tracking-[0.3em] normal-case",
              isCherry && "font-sans normal-case tracking-wider opacity-60"
            )}>
              <a href="#" className="hover:opacity-100 transition-opacity">SHOP</a>
              <a href="#" className="hover:opacity-100 transition-opacity">VISION</a>
              <a href="#" className="hover:opacity-100 transition-opacity">ARCHIVE</a>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <button 
              onClick={() => setIsCartOpen(true)}
              className={cn(
                "relative group flex items-center gap-4 px-6 py-2.5 rounded-full transition-all",
                isBrutalist ? "bg-black text-white" : 
                isCherry ? "bg-[#590D22] text-white" : 
                isMinimal ? "bg-black text-white shadow-sm hover:shadow-md" : 
                isTechnical ? "border border-[#00FF41] text-[#00FF41] bg-black hover:bg-[#00FF41]/10 shadow-[0_0_15px_rgba(0,255,65,0.2)]" : 
                "bg-black text-white"
              )}
            >
              <div className="flex flex-col items-end">
                <span className={cn(
                  "text-[8px] font-black tracking-widest leading-none",
                  isTechnical && "font-mono"
                )}>BAG_</span>
                <span className={cn(
                  "text-[10px] font-black",
                  isTechnical && "font-mono"
                )}>{cart.length} UNITS</span>
              </div>
              <ShoppingBag className={cn("w-5 h-5", isTechnical && "text-[#00FF41]")} />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        className={cn(
          "relative h-[80vh] flex items-center justify-center overflow-hidden transition-all duration-700",
          isBrutalist ? "border-b-2 border-black" : 
          isCherry ? "bg-[radial-gradient(circle_at_center,_white_0%,_transparent_100%)]" : 
          isMinimal ? "bg-white" : 
          isTechnical ? "border-b border-[#00FF41]/20 bg-[linear-gradient(rgba(0,255,65,0.03)_1px,_transparent_1px),_linear-gradient(90deg,rgba(0,255,65,0.03)_1px,_transparent_1px)] bg-[size:40px_40px] bg-black" : 
          "border-b border-black/5"
        )}
        style={isCherry ? { borderBottom: `2px dashed ${CHERRY_PALETTE.cherryBlossom}` } : {}}
      >
        <div 
          className={cn(
            "absolute inset-0 z-0 pointer-events-none",
            isBrutalist ? "opacity-10 grayscale" : 
            isCherry ? "opacity-20" : 
            isTechnical ? "opacity-40" : 
            "opacity-0"
          )}
        >
          {isBrutalist && (
            <>
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white" />
              <div className="grid grid-cols-6 h-full border-x-2 border-black/5">
                {[...Array(6)].map((_, i) => <div key={i} className="border-r-2 border-black/5" />)}
              </div>
            </>
          )}
          {isTechnical && (
            <div className="absolute inset-0">
               <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black" />
               <motion.div 
                 animate={{ opacity: [0.1, 0.4, 0.1] }}
                 transition={{ duration: 4, repeat: Infinity }}
                 className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,255,65,0.1)_0%,transparent_70%)]"
               />
               <div className="absolute inset-0 overflow-hidden pointer-events-none">
                 {[...Array(10)].map((_, i) => (
                   <motion.div
                     key={i}
                     initial={{ x: '-10%', y: `${Math.random() * 100}%` }}
                     animate={{ x: '110%' }}
                     transition={{ duration: 10 + Math.random() * 20, repeat: Infinity, ease: 'linear', delay: Math.random() * 20 }}
                     className="h-[1px] w-[20vw] bg-[#00FF41]/20"
                   />
                 ))}
               </div>
            </div>
          )}
          {isCherry && (
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  animate={{
                    y: [0, 800],
                    x: [0, Math.random() * 200 - 100],
                    rotate: [0, 360],
                    opacity: [0, 1, 0]
                  }}
                  transition={{
                    duration: 5 + Math.random() * 10,
                    repeat: Infinity,
                    delay: Math.random() * 5,
                    ease: "linear"
                  }}
                  className="absolute w-4 h-4 bg-[#FFB3C1] rounded-full blur-[2px]"
                  style={{ left: `${Math.random() * 100}%`, top: '-5%' }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="z-10 text-center max-w-5xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "circOut" }}
          >
            <h2 className={cn(
              "text-[12vw] md:text-[8vw] font-black tracking-tighter uppercase leading-[0.8] mb-8",
              isCherry && "font-serif italic capitalize tracking-normal text-[#590D22] drop-shadow-sm",
              isMinimal && "font-light tracking-[0.1em] normal-case",
              isTechnical && "font-mono tracking-tighter text-[#00FF41] animate-pulse"
            )}>
              {settings.customHeroTitle || (isCherry ? 'CHERRY.BLOOM' : isTechnical ? 'SYSTEM.INIT' : isMinimal ? 'The Collection' : 'THEORY.OFFLINE')}
            </h2>
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12">
              <p className={cn(
                "max-w-md text-sm md:text-base font-bold text-black/60 leading-relaxed uppercase tracking-tight text-center md:text-left",
                isCherry && "text-[#800F2F] normal-case italic font-medium",
                isMinimal && "font-light normal-case tracking-wide text-black/40",
                isTechnical && "text-[#00FF41]/60 font-mono text-xs normal-case tracking-widest"
              )}>
                {settings.customHeroDescription || 'A curation of architectural silhouettes and technical foundations for the modern landscape.'}
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={cn(
                  "px-8 py-5 rounded-full text-xs font-black uppercase tracking-[0.3em] flex items-center gap-4 group transition-all",
                  isCherry ? "bg-[#A4133C] text-white shadow-lg shadow-[#A4133C]/20" : 
                  isMinimal ? "bg-black text-white hover:bg-neutral-800" : 
                  isTechnical ? "bg-[#00FF41] text-black font-mono shadow-[0_0_20px_rgba(0,255,65,0.4)]" : 
                  "bg-black text-white"
                )}
              >
                Explore Collection
                <MoveRight className={cn(
                  "w-4 h-4 group-hover:translate-x-2 transition-transform", 
                  isCherry && "text-[#FFCCD5]",
                  isTechnical && "text-black"
                )} />
              </motion.button>
            </div>
          </motion.div>
        </div>

        {/* Floating Accents */}
        <div className="absolute bottom-12 left-12 hidden lg:block">
          <div className="space-y-4">
            <div className={cn(
              "flex items-center gap-4 text-[10px] font-black uppercase tracking-[0.2em] opacity-30",
              isCherry && "text-[#590D22] italic font-serif tracking-wide opacity-50"
            )}>
              <span className={cn("w-12 h-[2px]", isCherry ? "bg-[#590D22]" : "bg-black")} />
              AVAILABILITY & ARCHIVE
            </div>
            <p className={cn("text-[9px] font-bold opacity-40 max-w-[200px]", isCherry && "text-[#800F2F]")}>Fixed quantities. Weekly releases. Items of high desire are archived once sold out.</p>
          </div>
        </div>

        <div className="absolute bottom-12 right-12 hidden lg:block text-right">
          <div className={cn("text-sm font-black italic opacity-20", isCherry && "text-[#590D22] font-serif opacity-40")}>EST. 2026 / CHERRY.V1</div>
        </div>
      </section>

      {/* Filter Bar */}
      <div 
        className={cn(
          "sticky top-[68px] z-40 px-6 py-8 transition-all",
          isBrutalist ? "bg-white border-b-2 border-black" : 
          isCherry ? "bg-[#FFF0F3]/90 backdrop-blur-md border-b-2" : 
          isMinimal ? "bg-white/80 backdrop-blur-md" : 
          isTechnical ? "bg-black/90 backdrop-blur-md border-b border-[#00FF41]/20 shadow-[0_0_15px_rgba(0,255,65,0.1)]" : 
          "bg-white border-b border-black/5"
        )}
        style={isCherry ? { borderBottomColor: CHERRY_PALETTE.pastelPetal } : {}}
      >
        <div className="max-w-[1800px] mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-4 overflow-x-auto pb-4 md:pb-0 no-scrollbar w-full md:w-auto">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={cn(
                  "px-6 py-2.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap border-2",
                  activeCategory === cat ? 
                    (isCherry ? "bg-[#590D22] text-white border-[#590D22]" : isTechnical ? "bg-[#00FF41] text-black border-[#00FF41]" : isMinimal ? "bg-black text-white border-black" : "bg-black text-white border-black") : 
                    (isCherry ? "border-[#FFCCD5] text-[#590D22] hover:bg-[#FFB3C1]" : isTechnical ? "border-[#00FF41]/30 text-[#00FF41] hover:bg-[#00FF41]/10" : isMinimal ? "border-black/5 text-black/40 hover:border-black/20" : "border-black hover:bg-black hover:text-white")
                )}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="relative w-full md:w-80">
            <Search className={cn(
              "absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40", 
              isCherry && "text-[#590D22]",
              isTechnical && "text-[#00FF41] opacity-70"
            )} />
            <input 
              type="text"
              placeholder="SEARCH CATALOG_"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={cn(
                "w-full border-2 p-4 pl-12 rounded-xl outline-none focus:ring-0 font-black text-[10px] uppercase tracking-widest placeholder:opacity-30 transition-all",
                isBrutalist ? "bg-slate-50 border-black" : 
                isCherry ? "bg-white border-[#FFCCD5] text-[#590D22] focus:border-[#A4133C]" : 
                isMinimal ? "bg-[#F5F5F5] border-transparent focus:bg-white focus:border-black/10 text-black font-light" : 
                isTechnical ? "bg-black border-[#00FF41]/30 text-[#00FF41] focus:border-[#00FF41] placeholder:text-[#00FF41]/20 font-mono" : 
                "bg-slate-50 border-black/5 focus:border-black"
              )}
            />
          </div>
        </div>
      </div>

      {/* Product Grid */}
      <main className="max-w-[1800px] mx-auto p-6 md:p-12">
        <div className={cn(
          "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-1",
          isCherry && "gap-8",
          isMinimal && "gap-12",
          isTechnical && "gap-4"
        )}>
          <AnimatePresence mode="popLayout">
            {filteredItems.map((item, idx) => (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                key={item.id}
                onClick={() => setSelectedItem(item)}
                className={cn(
                  "group relative p-8 transition-all cursor-crosshair overflow-hidden",
                  isBrutalist ? "bg-white border-2 border-black hover:bg-slate-50" : 
                  isCherry ? "bg-white rounded-[2rem] shadow-[0_20px_50px_rgba(89,13,34,0.05)] hover:shadow-[0_20px_50px_rgba(89,13,34,0.12)] border-2 border-transparent hover:border-[#FFCCD5]" : 
                  isMinimal ? "bg-transparent border-none text-center grayscale hover:grayscale-0" : 
                  isTechnical ? "bg-black border border-[#00FF41]/20 hover:border-[#00FF41] shadow-[0_0_10px_rgba(0,255,65,0.05)] hover:shadow-[0_0_20px_rgba(0,255,65,0.1)] p-4" : 
                  "bg-white border hover:bg-slate-50"
                )}
              >
                <div className={cn(
                  "aspect-[4/5] mb-8 overflow-hidden transition-all duration-700",
                  isBrutalist ? "grayscale group-hover:grayscale-0" : 
                  isCherry ? "rounded-2xl shadow-inner bg-[#FFF0F3]/30" : 
                  isMinimal ? "rounded-none" : 
                  isTechnical ? "border border-[#00FF41]/10 bg-[#0A0A0A]" : 
                  "grayscale group-hover:grayscale-0"
                )}>
                  {item.imageUrl ? (
                    <img 
                      src={item.imageUrl} 
                      alt={item.name} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-slate-100">
                      <Package className="w-24 h-24 opacity-5" />
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-start gap-4">
                    <h3 className={cn(
                      "text-xl font-black uppercase tracking-tighter leading-none group-hover:translate-x-2 transition-transform",
                      isCherry && "text-[#590D22] font-serif italic normal-case tracking-normal",
                      isMinimal && "font-light tracking-[0.2em] normal-case",
                      isTechnical && "font-mono text-[#00FF41] lowercase tracking-widest translate-x-0 group-hover:translate-x-1"
                    )}>
                      {item.name}
                    </h3>
                    <span className={cn(
                      "text-[10px] font-black opacity-20", 
                      isCherry && "text-[#A4133C]",
                      isTechnical && "text-[#00FF41] font-mono"
                    )}>[{idx + 1}]</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <span className={cn(
                        "text-[9px] font-black uppercase tracking-[0.2em] opacity-30", 
                        isCherry && "text-[#800F2F]",
                        isTechnical && "text-[#00FF41]/40 font-mono"
                      )}>Price.Point</span>
                      <p className={cn(
                        "text-lg font-black tracking-tighter", 
                        isCherry && "text-[#A4133C]",
                        isTechnical && "text-[#00FF41] font-mono"
                      )}>
                        {settings.hidePrices ? 'CONTACT FOR PRICE' : `EGP ${(item.unitPrice || 0).toLocaleString()}`}
                      </p>
                    </div>
                    <div className={cn(
                      "p-3 rounded-full transition-all duration-300",
                      isCherry ? "bg-[#FFCCD5] text-[#590D22] opacity-100 group-hover:bg-[#A4133C] group-hover:text-white" : 
                      isTechnical ? "bg-[#00FF41] text-black rounded-none opacity-100 shadow-[0_0_10px_rgba(0,255,65,0.5)]" : 
                      "bg-black text-white opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0"
                    )}>
                      <Plus className="w-4 h-4" />
                    </div>
                  </div>
                </div>

                {/* Hover Details */}
                <div className={cn(
                  "absolute top-4 left-4 px-3 py-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity",
                  isBrutalist ? "bg-black text-white" : 
                  isCherry ? "bg-[#FFCCD5] text-[#590D22] font-bold text-[8px] rounded-full px-4" : 
                  isTechnical ? "bg-[#00FF41] text-black font-mono text-[8px]" : 
                  "bg-black text-white"
                )}>
                  <span className="text-[8px] font-black uppercase tracking-widest">{item.category}</span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {filteredItems.length === 0 && (
          <div className="py-40 text-center">
            <h3 className="text-4xl font-black uppercase tracking-tighter opacity-10">Empty.Manifest</h3>
            <p className="text-[10px] font-black uppercase tracking-widest mt-4 opacity-30">No products matching your requirements</p>
          </div>
        )}
      </main>

      {/* Product Detail Modal */}
      <AnimatePresence>
        {selectedItem && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedItem(null)}
              className="absolute inset-0 bg-white/80 backdrop-blur-xl"
            />
            <motion.div 
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              className={cn(
                "relative w-full max-w-7xl flex flex-col md:flex-row h-[90vh] md:h-[80vh] overflow-hidden transition-all duration-500",
                isBrutalist ? "bg-white border-4 border-black shadow-[30px_30px_0px_0px_rgba(0,0,0,1)]" : 
                isCherry ? "bg-white border-8 rounded-[3rem] shadow-2xl" : "bg-white border-4 border-black"
              )}
              style={isCherry ? { borderColor: CHERRY_PALETTE.lavenderBlush } : {}}
            >
              <div className={cn(
                "flex-1 relative overflow-hidden transition-all duration-1000",
                isBrutalist ? "bg-slate-100 border-r-4 border-black grayscale hover:grayscale-0" : 
                isCherry ? "bg-[#FFF0F3]/30 border-r-8" : "bg-slate-100 border-r-4 border-black grayscale hover:grayscale-0"
              )}
              style={isCherry ? { borderColor: CHERRY_PALETTE.lavenderBlush } : {}}
              >
                {selectedItem.imageUrl ? (
                  <img src={selectedItem.imageUrl} alt={selectedItem.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Package className="w-40 h-40 opacity-5" /></div>
                )}
                <button 
                  onClick={() => setSelectedItem(null)}
                  className={cn(
                    "absolute top-8 left-8 transition-all z-10",
                    isBrutalist ? "bg-white border-2 border-black p-4 hover:bg-black hover:text-white" : 
                    isCherry ? "bg-[#A4133C] text-white p-5 rounded-full hover:scale-110" : "bg-white border-2 border-black p-4 hover:bg-black hover:text-white"
                  )}
                >
                  <MoveRight className="w-6 h-6 rotate-180" />
                </button>
              </div>

              <div className="w-full md:w-[450px] p-12 flex flex-col h-full bg-white relative">
                <div className="flex-1 space-y-12 overflow-y-auto no-scrollbar">
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <span className={cn(
                        "px-4 py-1.5 border-2 text-[10px] font-black uppercase tracking-widest",
                        isBrutalist ? "border-black" : isCherry ? "border-[#FFCCD5] text-[#A4133C] rounded-full" : "border-black"
                      )}>
                        {selectedItem.category}
                      </span>
                      <span className={cn("text-[10px] font-black uppercase tracking-widest opacity-30", isCherry && "text-[#590D22]")}>
                        SKU: {selectedItem.code}
                      </span>
                    </div>
                    <h2 className={cn(
                      "text-6xl font-black tracking-tighter uppercase leading-[0.8]",
                      isCherry && "font-serif italic capitalize tracking-normal text-[#590D22]"
                    )}>{selectedItem.name}</h2>
                    {!settings.hidePrices && (
                      <p className={cn(
                        "text-4xl font-black tracking-tighter mt-4 italic",
                        isCherry ? "text-[#FF4D6D] tracking-normal font-sans" : ""
                      )}>
                        EGP {(selectedItem?.unitPrice || 0).toLocaleString()}
                      </p>
                    )}
                  </div>

                  <div className="space-y-6">
                    <div className={cn(
                      "flex items-center justify-between border-b-2 pb-4",
                      isBrutalist ? "border-black" : isCherry ? "border-[#FFCCD5]" : "border-black"
                    )}>
                      <span className={cn("text-[10px] font-black uppercase tracking-widest", isCherry && "text-[#590D22]")}>Description_</span>
                    </div>
                    <p className={cn(
                      "text-sm font-bold uppercase tracking-tight text-black/60 leading-relaxed",
                      isCherry && "text-[#800F2F] normal-case tracking-normal font-medium opacity-80"
                    )}>
                      {selectedItem.description || "A foundational piece engineered for versatility and resilience. Technical fabrications meet architectural tailoring."}
                    </p>
                  </div>

                  {selectedItem.barcode && (
                    <div className={cn(
                      "p-6 border-2 border-dashed rounded-xl",
                      isBrutalist ? "bg-slate-50 border-black" : isCherry ? "bg-[#FFF0F3] border-[#FFCCD5]" : "bg-slate-50 border-black"
                    )}>
                      <div className="flex items-center justify-between mb-4">
                        <span className={cn("text-[10px] font-black uppercase tracking-widest", isCherry && "text-[#590D22]")}>Authenticity_Check</span>
                        <Package className="w-4 h-4 opacity-20" />
                      </div>
                      <div className="font-mono text-xs font-black tracking-widest break-all opacity-40">
                        {selectedItem.barcode}
                      </div>
                    </div>
                  )}
                </div>

                <div className={cn(
                  "pt-12 mt-auto grid grid-cols-2 gap-4",
                  isBrutalist ? "border-t-4 border-black" : isCherry ? "border-t-4 border-double border-[#FFCCD5]" : "border-t-4 border-black"
                )}>
                  <button 
                    onClick={() => {
                      const url = `${window.location.origin}/#/menu/product/${selectedItem.id}`;
                      navigator.clipboard.writeText(url);
                      setCopied(true);
                      notify.confirm('Product link copied!');
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className={cn(
                      "py-6 rounded-sm font-black uppercase tracking-[0.2em] text-[10px] transition-all flex items-center justify-center gap-2",
                      isBrutalist ? "bg-white border-2 border-black text-black hover:bg-black hover:text-white" : 
                      isCherry ? "bg-white border-2 border-[#FFCCD5] text-[#590D22] hover:bg-[#FFF0F3] rounded-2xl" : 
                      "bg-white border-2 border-black text-black hover:bg-black hover:text-white"
                    )}
                  >
                    {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied' : 'Copy Link'}
                  </button>
                  <button 
                    onClick={() => setShowItemQR(!showItemQR)}
                    className={cn(
                      "py-6 rounded-sm font-black uppercase tracking-[0.2em] text-[10px] transition-all flex items-center justify-center gap-2",
                      isBrutalist ? "bg-white border-2 border-black text-black hover:bg-black hover:text-white" : 
                      isCherry ? "bg-white border-2 border-[#FFCCD5] text-[#590D22] hover:bg-[#FFF0F3] rounded-2xl" : 
                      "bg-white border-2 border-black text-black hover:bg-black hover:text-white"
                    )}
                  >
                    <QrCode className="w-4 h-4" />
                    {showItemQR ? 'Hide QR' : 'Show QR'}
                  </button>
                  
                  <button 
                    onClick={() => {
                      addToCart(selectedItem);
                      setSelectedItem(null);
                      setIsCartOpen(true);
                    }}
                    className={cn(
                      "col-span-2 py-6 rounded-sm font-black uppercase tracking-[0.4em] text-[10px] transition-all flex items-center justify-center gap-4",
                      isBrutalist ? "bg-black text-white hover:invert" : 
                      isCherry ? "bg-[#590D22] text-white hover:bg-[#800F2F] rounded-2xl shadow-lg" : 
                      "bg-black text-white hover:invert"
                    )}
                  >
                    Add to Bag
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  {showItemQR && (
                    <div className={cn(
                      "col-span-2 p-8 flex flex-col items-center justify-center animate-in fade-in zoom-in-95",
                      isBrutalist ? "bg-slate-50 border-2 border-black" : isCherry ? "bg-[#FFF0F3] border-4 border-double border-[#FFCCD5] rounded-3xl" : "bg-slate-50 border-2 border-black"
                    )}>
                       <div className={cn(
                         "p-4 bg-white mb-4",
                         isBrutalist ? "border-2 border-black" : isCherry ? "border-2 border-[#FFCCD5] rounded-2xl" : "border-2 border-black"
                       )}>
                         <QRCodeSVG 
                            value={`${window.location.origin}/#/menu/product/${selectedItem.id}`}
                            size={160}
                            level="H"
                         />
                       </div>
                       <p className={cn("text-[9px] font-black uppercase tracking-widest opacity-40", isCherry && "text-[#590D22]")}>Scan to view Product Landing Page_</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Shopping Bag Drawer */}
      <AnimatePresence>
        {isCartOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsCartOpen(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[110]"
            />
            <motion.div 
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className={cn(
                "fixed right-0 top-0 bottom-0 w-full max-w-[500px] z-[120] flex flex-col p-8 md:p-12 transition-all duration-500",
                isBrutalist ? "bg-white border-l-4 border-black" : 
                isCherry ? "bg-white/95 backdrop-blur-2xl border-l-[12px] border-double" : 
                "bg-white border-l-4 border-black"
              )}
              style={isCherry ? { borderLeftColor: CHERRY_PALETTE.lavenderBlush, color: CHERRY_PALETTE.nightBordeaux } : {}}
            >
              <div className="flex items-center justify-between mb-16 px-4">
                <h3 className={cn(
                  "text-4xl font-black tracking-tighter uppercase",
                  isCherry && "font-serif italic capitalize tracking-normal"
                )}>Shopping.Bag</h3>
                <button onClick={() => setIsCartOpen(false)} className="hover:scale-125 transition-transform">
                  <X className={cn("w-8 h-8", isCherry && "text-[#A4133C]")} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto pr-4 space-y-12">
                {cart.length === 0 ? (
                  <div className="py-20 text-center space-y-4 opacity-20">
                    <ShoppingCart className="w-16 h-16 mx-auto" />
                    <p className="font-black uppercase tracking-widest text-[10px]">Your bag is currently empty</p>
                  </div>
                ) : (
                  cart.map(({ item, quantity }) => (
                    <div key={item.id} className="flex gap-8 relative group">
                      <div className={cn(
                        "w-32 h-40 overflow-hidden flex-shrink-0 transition-all",
                        isBrutalist ? "bg-slate-100 border-2 border-black" : 
                        isCherry ? "bg-[#FFF0F3] border-2 border-[#FFCCD5] rounded-3xl shadow-sm group-hover:shadow-md" : 
                        "bg-slate-100 border-2 border-black"
                      )}>
                        {item.imageUrl && <img src={item.imageUrl} className={cn("w-full h-full object-cover transition-all grayscale", isCherry && "grayscale-0 group-hover:scale-110")} referrerPolicy="no-referrer" />}
                      </div>
                      <div className="flex-1 flex flex-col justify-between py-2">
                        <div className="space-y-1">
                          <h4 className={cn(
                            "text-lg font-black uppercase tracking-tighter leading-none",
                            isCherry && "font-serif italic capitalize tracking-normal"
                          )}>{item.name}</h4>
                          <p className={cn("text-[10px] font-bold opacity-30 uppercase tracking-widest", isCherry && "text-[#800F2F]")}>{item.category}</p>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className={cn(
                            "flex items-center rounded-full p-1 bg-white border-2",
                            isBrutalist ? "border-black" : isCherry ? "border-[#FFCCD5]" : "border-black"
                          )}>
                            <button onClick={() => updateQuantity(item.id, -1)} className="p-2 hover:bg-black/5 rounded-full"><Minus className="w-3 h-3" /></button>
                            <span className="px-4 text-xs font-black min-w-[40px] text-center">{quantity}</span>
                            <button onClick={() => updateQuantity(item.id, 1)} className="p-2 hover:bg-black/5 rounded-full"><Plus className="w-3 h-3" /></button>
                          </div>
                          <p className={cn("font-black text-sm uppercase", isCherry && "text-[#A4133C]")}>EGP {((item?.unitPrice || 0) * (quantity || 1)).toLocaleString()}</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => removeFromCart(item.id)}
                        className={cn(
                          "absolute -top-2 -right-2 bg-white border-2 p-1 hover:text-white transition-all opacity-0 group-hover:opacity-100",
                          isBrutalist ? "border-black hover:bg-red-500" : isCherry ? "border-[#FFCCD5] text-[#A4133C] hover:bg-[#A4133C] rounded-full" : "border-black hover:bg-red-500"
                        )}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div className={cn(
                "pt-12 mt-12 space-y-8",
                isBrutalist ? "border-t-8 border-black border-double" : 
                isCherry ? "border-t-[10px] border-double border-[#FFCCD5]" : 
                "border-t-8 border-black border-double"
              )}>
                <div className="flex items-center justify-between">
                  <span className={cn("text-[10px] font-black uppercase tracking-[0.3em]", isCherry && "text-[#800F2F]")}>Subtotal_</span>
                  <span className={cn(
                    "text-4xl font-black tracking-tighter uppercase italic",
                    isCherry && "text-[#590D22] tracking-normal"
                  )}>
                    EGP {(subtotal || 0).toLocaleString()}
                  </span>
                </div>
                <button 
                  onClick={handleCheckout} 
                  className={cn(
                    "w-full py-8 rounded-full font-black uppercase tracking-[0.5em] text-xs transition-all flex items-center justify-center gap-6",
                    isBrutalist ? "bg-black text-white hover:invert" : 
                    isCherry ? "bg-[#590D22] text-white hover:bg-[#800F2F] shadow-xl shadow-[#590D22]/20" : 
                    "bg-black text-white hover:invert"
                  )}
                >
                  Check_Out
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className={cn(
        "p-12 md:p-24 transition-all duration-700",
        isBrutalist ? "border-t-4 border-black bg-white" : 
        isCherry ? "bg-[#FFF0F3] border-t-8 border-double" : 
        "border-t border-black/5 bg-white"
      )}
      style={isCherry ? { borderTopColor: CHERRY_PALETTE.lavenderBlush } : {}}
      >
        <div className="max-w-[1800px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-24">
          <div className="md:col-span-2 space-y-12">
            <h2 className={cn(
              "text-8xl font-black tracking-tighter uppercase leading-[0.7]",
              isCherry && "text-[#590D22] font-serif italic capitalize tracking-normal"
            )}>
              {isCherry ? 'Cherry\n.Bloom' : 'THEORY\n.IRON'}
            </h2>
            <div className={cn(
              "flex gap-8 items-center text-[10px] font-black uppercase tracking-[0.3em] opacity-30",
              isCherry && "text-[#800F2F] opacity-60 italic font-medium tracking-wide"
            )}>
              <span className="hover:opacity-100 transition-opacity cursor-pointer">INSTAGRAM</span>
              <span className="hover:opacity-100 transition-opacity cursor-pointer">TWITTER</span>
              <span className="hover:opacity-100 transition-opacity cursor-pointer">NEWSLETTER</span>
            </div>
          </div>
          
          <div className="space-y-8">
            <h4 className={cn("text-[10px] font-black uppercase tracking-widest opacity-40", isCherry && "text-[#800F2F]")}>Information_</h4>
            <ul className={cn(
              "space-y-4 text-sm font-bold uppercase tracking-tight opacity-60",
              isCherry && "text-[#590D22] normal-case italic font-medium opacity-80"
            )}>
              <li className="hover:opacity-100 transition-opacity cursor-pointer">Archive</li>
              <li className="hover:opacity-100 transition-opacity cursor-pointer">Stockists</li>
              <li className="hover:opacity-100 transition-opacity cursor-pointer">Terms & Conditions</li>
              <li className="hover:opacity-100 transition-opacity cursor-pointer">Privacy Policy</li>
            </ul>
          </div>

          <div className="space-y-8">
            <h4 className={cn("text-[10px] font-black uppercase tracking-widest opacity-40", isCherry && "text-[#800F2F]")}>System_Status</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className={cn("w-2 h-2 animate-pulse rounded-full", isCherry ? "bg-[#FF4D6D]" : "bg-green-500")} />
                <span className={cn("text-[10px] font-black uppercase", isCherry && "text-[#590D22]")}>{isCherry ? 'Petals Active' : 'Core Online'}</span>
              </div>
              <p className={cn("text-[9px] font-bold opacity-30", isCherry && "text-[#800F2F]")}>VERSION {isCherry ? 'CHERRY.1.1' : '1.25.04.23'}</p>
              <p className={cn("text-[9px] font-bold opacity-30 tracking-tight leading-relaxed uppercase", isCherry && "text-[#800F2F] normal-case")}>
                {isCherry ? 'The bloom cycle is temporary. All creations are protected by digital signature.' : 'ALL ASSETS SECURED ON THE BLOCKCHAIN LEDGER. UNAUTHORIZED REPLICATION PROHIBITED.'}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicCatalogView;
